import {
  UseFieldArrayRemove,
  UseFieldArrayUpdate,
  UseFormGetValues,
  UseFormSetValue,
  UseFormTrigger
} from "react-hook-form";
import {
  useDeleteWasteManagementData,
  usePostWasteManagementData,
  usePutWasteManagementData
} from "../api/wasteManagement";
import {
  WASTE_MANAGEMENT_TYPE_IDS,
  WASTE_VALIDATION_MESSAGES
} from "../components/wasteManagment/wasteManagement.constants";
import {
  validateMoistureRange,
  validateWasteStreamName
} from "../components/wasteManagment/wasteManagement.validation";
import { IWasteForm, IWasteRecord } from "../components/wasteManagment/wasteManagement.types";
import {
  buildWasteApiPayload,
  createEmptyWasteRecord,
  normalizeWasteUnit
} from "../components/wasteManagment/wasteManagement.utils";
import { MEASUREMENT_TYPES } from "../config/enums/MeasurementTypes";
import { PAGINATION_ITEMS_PER_PAGE } from "../config/constants";
import { errorView, successMessage } from "../helpers/ErrorHandler";

interface UseWasteCrudParams {
  processId: string;
  type: string;
  supplierDataHandle: boolean;
  defaultTypeId: string;
  getValues: UseFormGetValues<IWasteForm>;
  setValue: UseFormSetValue<IWasteForm>;
  trigger: UseFormTrigger<IWasteForm>;
  remove: UseFieldArrayRemove;
  update: UseFieldArrayUpdate<IWasteForm, "wastes">;
  fieldsLength: number;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  editingId: string | number | null;
  setEditingId: (id: string | number | null) => void;
  refetchWasteData: () => void;
  onWasteManagementSave: (processId: string) => void;
}

export const useWasteCrud = ({
  processId,
  type,
  supplierDataHandle,
  defaultTypeId,
  getValues,
  setValue,
  trigger,
  remove,
  update,
  fieldsLength,
  currentPage,
  setCurrentPage,
  editingId,
  setEditingId,
  refetchWasteData,
  onWasteManagementSave
}: UseWasteCrudParams) => {
  const { mutate: postWasteDataSet } = usePostWasteManagementData();
  const { mutate: putWasteDataSet } = usePutWasteManagementData();
  const { mutate: deleteWasteDataSet } = useDeleteWasteManagementData();

  const resetDraftForm = (lastIndex: number) => {
    const defaultUnit = normalizeWasteUnit(
      getValues("sideStreamUnit"),
      MEASUREMENT_TYPES.WEIGHT_KG
    );
    update(lastIndex, createEmptyWasteRecord(defaultTypeId, defaultUnit));
  };

  const validateWasteBusinessRules = (recordData: IWasteRecord): string | null => {
    if (!recordData.sideStreamsDistribution || parseFloat(recordData.sideStreamsDistribution) <= 0)
      return WASTE_VALIDATION_MESSAGES.DISTRIBUTION_POSITIVE;

    if (!recordData.managementName || recordData.managementName.trim() === "")
      return WASTE_VALIDATION_MESSAGES.STREAM_NAME_REQUIRED;

    const streamNameValidation = validateWasteStreamName(recordData.managementName);
    if (streamNameValidation !== true) return streamNameValidation;

    if (recordData.managementTypeId === WASTE_MANAGEMENT_TYPE_IDS.RECOVERED_FOR_USE_OR_SALE) {
      const moistureValidation = validateMoistureRange(recordData.moistureContent);
      if (moistureValidation !== true) return moistureValidation;
    }

    return null;
  };

  const createWasteRecord = (
    index: number,
    recordData: IWasteRecord,
    payload: any
  ): Promise<boolean> =>
    new Promise(resolve => {
      postWasteDataSet(
        { processingId: processId, type, supplierDataHandle, wasteData: payload },
        {
          onSuccess: (response: any) => {
            successMessage("Waste Management created successfully");
            onWasteManagementSave(processId);
            refetchWasteData();
            if (response.data?._id) setValue(`wastes.${index}._id`, response.data._id);
            resolve(true);
          },
          onError: (error: any) => {
            errorView(error?.msg || "Failed to create waste management");
            resolve(false);
          }
        }
      );
    });

  const updateWasteRecord = (wasteManagementId: string, payload: any): Promise<boolean> =>
    new Promise(resolve => {
      putWasteDataSet(
        { wasteManagementId, type, supplierDataHandle, wasteData: payload },
        {
          onSuccess: () => {
            successMessage("Waste Management updated successfully");
            onWasteManagementSave(processId);
            refetchWasteData();
            resolve(true);
          },
          onError: (error: any) => {
            errorView(error?.msg || "Failed to update waste management");
            resolve(false);
          }
        }
      );
    });

  const persistWasteRecord = (index: number, recordData: IWasteRecord): Promise<boolean> => {
    const payload = buildWasteApiPayload(recordData);
    return recordData._id
      ? updateWasteRecord(recordData._id as string, payload)
      : createWasteRecord(index, recordData, payload);
  };

  const handleSaveWaste = async (index: number): Promise<boolean> => {
    const isValid = await trigger([
      `wastes.${index}.managementName` as any,
      `wastes.${index}.managementTypeId` as any,
      `wastes.${index}.sideStreamsDistribution` as any,
      `wastes.${index}.unit` as any,
      `wastes.${index}.moistureContent` as any
    ]);
    if (!isValid) {
      return false;
    }

    const recordData: IWasteRecord = getValues(`wastes.${index}`);
    const validationError = validateWasteBusinessRules(recordData);
    if (validationError) {
      errorView(validationError);
      return false;
    }

    return persistWasteRecord(index, recordData);
  };
  const handleAddWasteRecord = async () => {
    const lastIndex = fieldsLength - 1;
    // handleSaveWaste already runs trigger with explicit field paths and shows
    // field-level validation errors. Calling it directly removes the duplicate
    // trigger that previously ran here and could conflict with state updates
    // happening between the two async suspend points.
    const saved = await handleSaveWaste(lastIndex);
    if (saved) {
      resetDraftForm(lastIndex);
    }
  };

  const handleRemoveWasteRecord = (index: number, id?: string | number) => {
    if (id) {
      deleteWasteDataSet(
        {
          type,
          supplierDataHandle,
          wasteData: {
            wasteManagementId: id as string,
            processingId: processId
          }
        },
        {
          onSuccess: () => {
            successMessage("Waste Management deleted successfully");
            onWasteManagementSave(processId);
            refetchWasteData();
            remove(index);

            // Adjust the current page down if the deletion emptied the last page.
            const newTotalPages = Math.ceil((fieldsLength - 1) / PAGINATION_ITEMS_PER_PAGE);
            if (currentPage > newTotalPages && newTotalPages > 0) {
              setCurrentPage(newTotalPages);
            }

            // Exit edit mode if we just deleted the record that was being edited.
            if (editingId === id) {
              setEditingId(null);
            }
          },
          onError: (error: any) => {
            errorView(error?.msg || "Failed to delete waste management");
          }
        }
      );
    } else {
      // Draft row: clear its fields without removing the row from the array.
      resetDraftForm(fieldsLength - 1);
    }
  };

  return { handleSaveWaste, handleAddWasteRecord, handleRemoveWasteRecord };
};
